import React, { useEffect, useState } from "react";
import {
  Paper,
  MuiThemeProvider,
  Button,
  Chip,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { bindActionCreators } from "redux";
import MUIDataTable from "mui-datatables";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import FilterDataTable from "../../../utils/FilterDataTable";
import { tableMuiTheme } from "../../table/table_styles";
import MasterSportsDialog from "./MasterSportsDialog";

import {
  getMasterSports,
  getMasterSport,
  defaultMasterSport,
} from "../../../crud/api/sports.crud";
import { setLoader } from "../../../../redux/ducks/load.duck";
import * as sportDUCK from "../../../../redux/ducks/sport.duck";

const MasterSports = ({ masterSports, settingLoader, sportActions }) => {
  const isDesktop = useMediaQuery("(min-width: 992px)");
  const isTablet = useMediaQuery("(min-width: 768px)");
  const [dialog, setDialog] = useState("close");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (query && query.length > 1) {
      getMasterSports(sportActions.setMasterSports, 1, query);
    } else {
      getMasterSports(sportActions.setMasterSports);
    }
  }, [query, sportActions.setMasterSports]);

  const columns = [
    {
      name: "id",
      label: "id",
      options: { filter: false, sort: false, display: false },
    },
    {
      name: "name",
      label: "Name",
      options: { filter: false, sort: false },
    },
    {
      name: "description",
      label: "Description",
      options: {
        filter: false,
        sort: false,
        // MUIDataTable cells default to nowrap + ellipsis. Description
        // is the only long-text column, so let it wrap and give it a
        // sensible max width so it doesn't push Color/Status off-
        // screen on small viewports.
        setCellProps: () => ({
          style: { whiteSpace: "normal", maxWidth: 480, minWidth: 240 },
        }),
        customBodyRender: (value) => value || "—",
      },
    },
    {
      name: "color",
      label: "Color",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => (
          <div
            style={{
              background: value,
              height: 20,
              width: 40,
              borderRadius: 20,
            }}
          />
        ),
      },
    },
    {
      name: "archived_at",
      label: "Status",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) =>
          value ? (
            <Chip label="Archived" size="small" />
          ) : (
            <Chip label="Active" color="primary" size="small" />
          ),
      },
    },
  ];

  const options = {
    serverSide: true,
    rowsPerPageOptions: [],
    rowsPerPage: masterSports.rowsPerPage,
    // API returns 1-based page (`page: 1` = first page); MUIDataTable
    // works in 0-based indexes. Without the -1, clicking "Previous"
    // from the last page is a no-op: MUIDataTable thinks we're one
    // page past the end, asks to step back to what it considers the
    // current page, onChangePage(mPage)+1 re-requests the same page,
    // and the view never moves. Only visible at >2 pages of data.
    page: Math.max(0, (masterSports.page || 1) - 1),
    count: masterSports.count,
    search: false,
    sort: false,
    print: false,
    filter: false,
    viewColumns: false,
    download: false,
    selectableRows: "none",
    tableBodyMaxHeight: isDesktop ? "670px" : isTablet ? "640px" : "357px",
    textLabels: {
      body: { noMatch: "No master sports yet" },
      pagination: {
        next: "Next",
        previous: "Previous",
        rowsPerPage: "Rows:",
        displayRows: "of",
      },
    },
    onRowClick: (data) => openDialog("editing", data[0]),
    onChangePage: (mPage) => {
      settingLoader(true);
      getMasterSports(sportActions.setMasterSports, mPage + 1, query);
    },
  };

  const openDialog = (mode, id = null) => {
    switch (mode) {
      case "create":
        settingLoader(true);
        sportActions.setMasterSport(defaultMasterSport);
        setDialog(mode);
        break;
      case "editing":
        settingLoader(true);
        getMasterSport(sportActions.setMasterSport, id).then(() =>
          setDialog(mode)
        );
        break;
      case "close":
        setDialog(mode);
        setTimeout(() => {
          sportActions.setMasterSport(defaultMasterSport);
        }, 500);
        break;
      default:
        console.error("Invalid dialog mode");
    }
  };

  return (
    <div className="table-container">
      <div className="pb-4">
        <Typography variant="h6">Master Sport List</Typography>
        <Typography variant="caption" color="textSecondary">
          Zone Super Admin only. This is the canonical sport list that every
          school draws from. Standardized names — schools pick from here.
        </Typography>
      </div>
      <div className="pb-4 d-flex justify-content-between align-items-end">
        <FilterDataTable query={query} setQuery={setQuery} />
        <Button
          id="create_master_sport_btn"
          variant="contained"
          color="secondary"
          onClick={() => openDialog("create")}
        >
          <Add /> New master sport
        </Button>
      </div>
      <Paper>
        <MuiThemeProvider theme={tableMuiTheme()}>
          <MUIDataTable
            columns={columns}
            data={masterSports.data}
            options={options}
          />
        </MuiThemeProvider>
      </Paper>
      <MasterSportsDialog mode={dialog} setDialog={openDialog} />
    </div>
  );
};

MasterSports.propTypes = {
  masterSports: PropTypes.shape({
    data: PropTypes.array,
    count: PropTypes.number,
    page: PropTypes.number,
    rowsPerPage: PropTypes.number,
  }).isRequired,
  settingLoader: PropTypes.func.isRequired,
  sportActions: PropTypes.object.isRequired,
};

const mapStateToProps = (store) => ({
  masterSports: store.sport.master_sports,
});

const mapDispatchToProps = (dispatch) => ({
  settingLoader: bindActionCreators(setLoader, dispatch),
  sportActions: bindActionCreators(sportDUCK.actions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(MasterSports);
